import {
  useCreate,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { toIsoDay } from "@/components/market-intel/dates";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  normalizeTrendForm,
  RELEVANCES,
  relevanceLabelKey,
  trendFormDefaults,
  type TrendFormValues,
} from "./intel";
import { PLATFORMS, platformLabelKey } from "./platforms";
import type { MarketTrend, SocialPlatform, TrendRelevance } from "./types";
import { FormActions } from "./ui";

export const TrendForm = ({
  open,
  onOpenChange,
  trend,
  competitors,
  defaultCompetitorId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edits this trend; creates one when null. */
  trend: MarketTrend | null;
  competitors: readonly MarketCompetitor[];
  /** Preselected competitor of a new trend (from its page). */
  defaultCompetitorId?: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const today = toIsoDay(new Date());
  const [values, setValues] = useState(() => trendFormDefaults(today));
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(
        trendFormDefaults(today, trend ?? undefined, defaultCompetitorId),
      );
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trend, defaultCompetitorId]);

  const set =
    <K extends keyof TrendFormValues>(key: K) =>
    (value: TrendFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeTrendForm(
      values,
      competitors.map((competitor) => competitor.id),
    );
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("competition.form.error", { type: "error" }),
    };
    if (trend)
      update(
        "market_trends",
        { id: trend.id, data: result.data, previousData: trend },
        options,
      );
    else create("market_trends", { data: result.data }, options);
  };

  const errorId = `${formId}-error`;
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        trend ? "competition.trends.edit" : "competition.trends.add",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      <Field
        id={`${formId}-title`}
        label={translate("competition.trends.form.title")}
      >
        <Input
          id={`${formId}-title`}
          value={values.title}
          onChange={(event) => set("title")(event.target.value)}
          aria-invalid={
            error === "competition.form.title_required" || undefined
          }
          aria-describedby={error ? errorId : undefined}
          autoFocus={!isMobile}
          maxLength={220}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field
          id={`${formId}-platform`}
          label={translate("competition.platforms.label")}
        >
          <select
            id={`${formId}-platform`}
            className={SELECT_CLASS}
            value={values.platform}
            onChange={(event) =>
              set("platform")(event.target.value as SocialPlatform)
            }
          >
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {translate(platformLabelKey(platform))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-relevance`}
          label={translate("competition.trends.relevance.label")}
        >
          <select
            id={`${formId}-relevance`}
            className={SELECT_CLASS}
            value={values.relevance}
            onChange={(event) =>
              set("relevance")(event.target.value as TrendRelevance)
            }
          >
            {RELEVANCES.map((relevance) => (
              <option key={relevance} value={relevance}>
                {translate(relevanceLabelKey(relevance))}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        id={`${formId}-description`}
        label={translate("competition.trends.form.description")}
      >
        <Textarea
          id={`${formId}-description`}
          value={values.description}
          onChange={(event) => set("description")(event.target.value)}
          rows={3}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${formId}-detected_at`}
          label={translate("competition.trends.form.detected_at")}
        >
          <DateField
            id={`${formId}-detected_at`}
            value={values.detected_at}
            onChange={(event) => set("detected_at")(event.target.value)}
          />
        </Field>
        <Field
          id={`${formId}-competitor`}
          label={translate("competition.trends.form.competitor")}
        >
          <select
            id={`${formId}-competitor`}
            className={SELECT_CLASS}
            value={values.competitor_id}
            onChange={(event) => set("competitor_id")(event.target.value)}
          >
            <option value="">{translate("competition.form.none")}</option>
            {competitors.map((competitor) => (
              <option key={competitor.id} value={String(competitor.id)}>
                {competitor.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        id={`${formId}-url`}
        label={translate("competition.trends.form.url")}
      >
        <Input
          id={`${formId}-url`}
          type="url"
          inputMode="url"
          value={values.url}
          onChange={(event) => set("url")(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
};
